import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowUpRight, Factory, Layers, Pencil, Plus, Target, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Switch } from '@/components/ui/Switch';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { MobileListCard, MobileListCardStack } from '@/components/ui/MobileListCard';
import { useIsMobile } from '@/lib/useMediaQuery';
import { fmtQty } from '@/features/manufacturing/planning/engine';
import { endOfMonth, monthKey } from '@/features/manufacturing/planning/dates';
import {
  PLAN_LINE_KIND_LABELS,
  useDeletePlanLine,
  useSetPlanLineActive,
  type PlanLineRow,
} from '@/features/manufacturing/planning/hooks';
import type { PlanningModel } from '@/features/manufacturing/planning/types';
import { cn } from '@/lib/utils';
import { monthLabel, SectionTitle, shortDate } from './shared';

interface PlanTabProps {
  model: PlanningModel;
  planLines: PlanLineRow[];
  canEdit: boolean;
  onAdd: () => void;
  onEdit: (line: PlanLineRow) => void;
}

/**
 * The demand side: every target on file with the per-day rate it turns
 * into, and what that adds up to per finished good and sub-assembly.
 */
export function PlanTab({ model, planLines, canEdit, onAdd, onEdit }: PlanTabProps) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const setActive = useSetPlanLineActive();
  const remove = useDeletePlanLine();
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const scheduleByLine = useMemo(
    () => new Map(model.schedules.map((s) => [s.line.id, s])),
    [model.schedules],
  );

  const months = useMemo(() => {
    const keys: string[] = [];
    for (const date of model.dailyProduction.keys()) {
      const key = monthKey(date);
      if (!keys.includes(key)) keys.push(key);
    }
    return keys.sort().slice(0, 4);
  }, [model.dailyProduction]);

  const buildRows = useMemo(() => {
    const perItem = new Map<string, Map<string, number>>();
    for (const [date, builds] of model.dailyProduction) {
      const key = monthKey(date);
      for (const build of builds) {
        const byMonth = perItem.get(build.itemId) ?? new Map<string, number>();
        byMonth.set(key, (byMonth.get(key) ?? 0) + build.quantity);
        perItem.set(build.itemId, byMonth);
      }
    }
    return [...perItem.entries()]
      .map(([itemId, byMonth]) => {
        const plan = model.itemsById.get(itemId);
        const thisMonth = months[0];
        const workingDaysLeft = thisMonth
          ? model.calendar.workingDaysBetween(model.today, endOfMonth(`${thisMonth}-01`)).length
          : 0;
        const thisMonthQty = thisMonth ? (byMonth.get(thisMonth) ?? 0) : 0;
        return {
          itemId,
          name: plan?.item.name ?? 'Item',
          sku: plan?.item.sku ?? '',
          onHand: plan?.onHand ?? 0,
          total: [...byMonth.values()].reduce((a, b) => a + b, 0),
          byMonth,
          perDayThisMonth: workingDaysLeft > 0 ? thisMonthQty / workingDaysLeft : 0,
          hasBom: plan ? plan.role !== 'purchased' && plan.plannedProduction.length > 0 : false,
          missingBom: plan?.gaps.some((g) => g.kind === 'missing_bom') ?? false,
        };
      })
      .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
  }, [model, months]);

  const subAssemblies = useMemo(
    () =>
      model.items
        .filter((p) => p.role === 'sub_assembly')
        .map((p) => ({
          plan: p,
          toBuild: p.plannedProduction.reduce((a, b) => a + b.quantity, 0),
        }))
        .sort((a, b) => b.plan.grossRequirement - a.plan.grossRequirement),
    [model.items],
  );

  const activeLines = planLines.filter((l) => l.is_active);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <CardTitle>Production targets</CardTitle>
              <p className="text-sm text-muted-foreground">
                {activeLines.length === 0
                  ? 'Nothing planned yet.'
                  : `${activeLines.length} active target${activeLines.length === 1 ? '' : 's'} · ${fmtQty(
                      model.summary.unitsPlanned,
                    )} units over the next ${model.settings.horizonDays} days`}
              </p>
            </div>
            {canEdit && (
              <Button type="button" size="sm" onClick={onAdd} className="shrink-0">
                <Plus className="h-4 w-4" />
                Add target
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {planLines.length === 0 ? (
            <EmptyState
              icon={Target}
              title="Tell the planner what to build"
              action={
                canEdit ? (
                  <Button type="button" onClick={onAdd}>
                    <Plus className="h-4 w-4" />
                    Add a target
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Build</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead>Window</TableHead>
                      <TableHead className="text-right">Per working day</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Active</TableHead>
                      {canEdit && <TableHead className="w-20" />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {planLines.map((line) => {
                      const schedule = scheduleByLine.get(line.id);
                      return (
                        <TableRow key={line.id} className={cn(!line.is_active && 'opacity-50')}>
                          <TableCell>
                            <div className="font-medium">{line.items?.name ?? 'Item'}</div>
                            <div className="font-mono text-[11px] text-muted-foreground">
                              {line.items?.sku}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{PLAN_LINE_KIND_LABELS[line.kind]}</Badge>
                            {line.net_against_stock && (
                              <div className="mt-1 text-[11px] text-muted-foreground">
                                net of stock
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {fmtQty(line.quantity)}
                            {line.kind === 'daily' && (
                              <span className="text-xs text-muted-foreground"> / day</span>
                            )}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-sm">
                            {line.kind === 'monthly' ? (
                              monthLabel(line.start_date ?? model.today)
                            ) : (
                              <>
                                {shortDate(schedule?.firstDate ?? line.start_date ?? model.today)}
                                <span className="text-muted-foreground"> → </span>
                                {line.end_date
                                  ? shortDate(line.end_date)
                                  : line.kind === 'daily'
                                    ? 'ongoing'
                                    : shortDate(schedule?.lastDate)}
                              </>
                            )}
                            {schedule && schedule.issues.length > 0 && (
                              <div className="text-[11px] text-fresh-salmon-dark">
                                {schedule.issues[0][0].toUpperCase() + schedule.issues[0].slice(1)}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {schedule && line.is_active ? (
                              <>
                                {fmtQty(schedule.perDay)}
                                <div className="text-[11px] text-muted-foreground">
                                  {schedule.workingDays} working day
                                  {schedule.workingDays === 1 ? '' : 's'}
                                </div>
                              </>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="max-w-[180px] truncate text-sm text-muted-foreground">
                            {line.source ?? '—'}
                          </TableCell>
                          <TableCell>
                            <Switch
                              checked={line.is_active}
                              disabled={!canEdit || setActive.isPending}
                              aria-label={line.is_active ? 'Deactivate target' : 'Activate target'}
                              onCheckedChange={(checked) =>
                                setActive.mutate(
                                  { id: line.id, isActive: checked },
                                  {
                                    onError: (err) =>
                                      toast.error(
                                        err instanceof Error ? err.message : 'Could not update.',
                                      ),
                                  },
                                )
                              }
                            />
                          </TableCell>
                          {canEdit && (
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  aria-label="Edit target"
                                  onClick={() => onEdit(line)}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className={cn(
                                    'h-7 w-7',
                                    confirmDelete === line.id && 'text-fresh-salmon-dark',
                                  )}
                                  aria-label={
                                    confirmDelete === line.id ? 'Confirm delete' : 'Delete target'
                                  }
                                  title={
                                    confirmDelete === line.id ? 'Click again to delete' : 'Delete'
                                  }
                                  onClick={() => {
                                    if (confirmDelete !== line.id) {
                                      setConfirmDelete(line.id);
                                      window.setTimeout(
                                        () => setConfirmDelete((c) => (c === line.id ? null : c)),
                                        3000,
                                      );
                                      return;
                                    }
                                    remove.mutate(line.id, {
                                      onSuccess: () => toast.success('Target removed'),
                                      onError: (err) =>
                                        toast.error(
                                          err instanceof Error ? err.message : 'Could not delete.',
                                        ),
                                    });
                                    setConfirmDelete(null);
                                  }}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <ul className="space-y-2 md:hidden">
                {planLines.map((line) => {
                  const schedule = scheduleByLine.get(line.id);
                  return (
                    <li
                      key={line.id}
                      className={cn(
                        'rounded-lg border border-border bg-card p-3',
                        !line.is_active && 'opacity-50',
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="truncate font-medium">{line.items?.name ?? 'Item'}</div>
                          <div className="text-xs text-muted-foreground">
                            {PLAN_LINE_KIND_LABELS[line.kind]} · {fmtQty(line.quantity)}
                            {line.kind === 'daily' ? '/day' : ''}
                          </div>
                        </div>
                        <Switch
                          checked={line.is_active}
                          disabled={!canEdit}
                          aria-label="Active"
                          onCheckedChange={(checked) =>
                            setActive.mutate({ id: line.id, isActive: checked })
                          }
                        />
                      </div>
                      {schedule && line.is_active && (
                        <div className="mt-2 text-xs text-muted-foreground">
                          {fmtQty(schedule.perDay)} per working day ·{' '}
                          {shortDate(schedule.firstDate)} → {shortDate(schedule.lastDate)}
                        </div>
                      )}
                      {canEdit && (
                        <div className="mt-2 flex justify-end gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => onEdit(line)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            Edit
                          </Button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </CardContent>
      </Card>

      {buildRows.length > 0 && (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Factory className="h-4 w-4 text-info" />
                Build schedule
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <SectionTitle
                hint={`Working days: ${model.calendar.workingDaysBetween(model.today, model.horizonEnd).length} in horizon`}
              >
                Units to build per month
              </SectionTitle>
              {isMobile ? (
                <MobileListCardStack>
                  {buildRows.map((row) => (
                    <MobileListCard
                      key={row.itemId}
                      title={row.name}
                      subtitle={row.sku}
                      badges={row.missingBom ? <Badge variant="warning">No BOM</Badge> : undefined}
                      amount={{
                        value: row.perDayThisMonth > 0 ? fmtQty(row.perDayThisMonth) : '—',
                        caption: 'per day now',
                      }}
                      meta={months.map((m) => ({
                        label: monthLabel(m),
                        value: row.byMonth.get(m) ? fmtQty(row.byMonth.get(m)!) : '—',
                      }))}
                      trailing={null}
                    />
                  ))}
                </MobileListCardStack>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Finished good</TableHead>
                      <TableHead className="text-right">Per day now</TableHead>
                      {months.map((m) => (
                        <TableHead key={m} className="text-right">
                          {monthLabel(m)}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {buildRows.map((row) => (
                      <TableRow key={row.itemId}>
                        <TableCell>
                          <Link
                            to={`/catalog/items/${row.itemId}`}
                            className="font-medium hover:underline"
                          >
                            {row.name}
                          </Link>
                          {row.missingBom && (
                            <Badge variant="warning" className="ml-2">
                              No BOM
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.perDayThisMonth > 0 ? fmtQty(row.perDayThisMonth) : '—'}
                        </TableCell>
                        {months.map((m) => (
                          <TableCell key={m} className="text-right tabular-nums">
                            {row.byMonth.get(m) ? (
                              fmtQty(row.byMonth.get(m)!)
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-info" />
                Sub-assemblies
              </CardTitle>
            </CardHeader>
            <CardContent>
              {subAssemblies.length === 0 ? (
                <p className="text-sm text-muted-foreground">No sub-assemblies in these BOMs.</p>
              ) : isMobile ? (
                <MobileListCardStack>
                  {subAssemblies.map(({ plan, toBuild }) => (
                    <MobileListCard
                      key={plan.item.id}
                      title={plan.item.name}
                      subtitle={
                        plan.usedIn.length > 0
                          ? `in ${plan.usedIn.map((u) => model.itemsById.get(u.itemId)?.item.name ?? 'assembly').join(', ')}`
                          : plan.item.sku
                      }
                      amount={{
                        value: toBuild > 0 ? fmtQty(toBuild) : 'Covered',
                        caption: toBuild > 0 ? 'to build' : 'by stock',
                        tone: toBuild > 0 ? 'neutral' : 'positive',
                      }}
                      meta={[
                        { label: 'Needed', value: fmtQty(plan.grossRequirement) },
                        { label: 'On hand', value: fmtQty(plan.onHand) },
                      ]}
                      onTap={() => navigate(`/mfg/boms/${plan.item.id}`)}
                    />
                  ))}
                </MobileListCardStack>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sub-assembly</TableHead>
                      <TableHead className="text-right">Needed</TableHead>
                      <TableHead className="text-right">On hand</TableHead>
                      <TableHead className="text-right">To build</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subAssemblies.map(({ plan, toBuild }) => (
                      <TableRow key={plan.item.id}>
                        <TableCell>
                          <Link
                            to={`/mfg/boms/${plan.item.id}`}
                            className="inline-flex items-center gap-1 font-medium hover:underline"
                          >
                            {plan.item.name}
                            <ArrowUpRight className="h-3 w-3 text-muted-foreground" />
                          </Link>
                          {plan.usedIn.length > 0 && (
                            <div className="text-[11px] text-muted-foreground">
                              in{' '}
                              {plan.usedIn
                                .map((u) => model.itemsById.get(u.itemId)?.item.name ?? 'assembly')
                                .join(', ')}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtQty(plan.grossRequirement)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtQty(plan.onHand)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {toBuild > 0 ? (
                            fmtQty(toBuild)
                          ) : (
                            <span className="text-bright-moss-dark">covered by stock</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
